import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { PICKUP_FILTER_START, SAME_DAY_FILTER_START, SHIP_TO_HOME_FILTER_START } from 'constants/UpperFunnel';
import HelperUtils from 'utils/Helpers';
import StringUtils from 'utils/String';
import Date from 'utils/Date';
import sysUtils from 'utils/ShopYourStore';
import deliveryOptionsUtils from 'utils/DeliveryOptions';
import preferredStoreSelector from 'selectors/user/preferredStoreSelector';
import { userSelector } from 'selectors/user/userSelector';

import { showEddOnBrowseAndSearchSelector } from 'viewModel/selectors/testTarget/showEddOnBrowseAndSearchSelector';
const { wrapHOC } = FrameworkUtils;
const { getLocalisedDataFromResource, getTextFromResource } = LanguageLocaleUtils;
const { truncateText: originalTruncateText } = HelperUtils;
const { isNDDNewDeliveryName } = deliveryOptionsUtils;

import EN from 'components/Catalog/UpperFunnel/UpperFunnelProductTiles/locales/UpperFunnelProductTiles_en_US.js';
import FR from 'components/Catalog/UpperFunnel/UpperFunnelProductTiles/locales/UpperFunnelProductTiles_fr_CA.js';
const getText = getLocalisedDataFromResource(EN, FR);
const STORE_NAME_MAX_LENGTH = 30;
const truncateText = text => originalTruncateText(text, STORE_NAME_MAX_LENGTH);

const fields = createSelector(
    createStructuredSelector({
        preferredStore: preferredStoreSelector,
        user: userSelector,
        curbsidePickupText: getTextFromResource(getText, 'curbsideAvailable'),
        sameDayDeliveryText: getTextFromResource(getText, 'sameDayDelivery'),
        fastDeliveryText: getTextFromResource(getText, 'fastDelivery'),
        shipToHomeTitle: getTextFromResource(getText, 'shipToHomeTitle'),
        shipToHomeShipMessage: getTextFromResource(getText, 'shipToHomeShipMessage', ['{0}', '{1}']),
        showEddOnBrowseAndSearch: showEddOnBrowseAndSearchSelector
    }),
    (_, ownProps) => ownProps.checkedRefinements,
    (_, ownProps) => ownProps.deliveryOptions,
    (_, ownProps) => ownProps.pickupEligible,
    (_, ownProps) => ownProps.pickupNearbyEligible,
    (_, ownProps) => ownProps.sameDayEligible,
    (_, ownProps) => ownProps.shipToHomeEligible,
    (_, ownProps) => ownProps.bypassShowEddOnBrowseAndSearchAbTest,
    // eslint-disable-next-line complexity
    (
        {
            preferredStore,
            user,
            curbsidePickupText,
            sameDayDeliveryText,
            fastDeliveryText,
            shipToHomeTitle,
            shipToHomeShipMessage,
            showEddOnBrowseAndSearch
        },
        checkedRefinements,
        deliveryOptions,
        pickupEligible,
        pickupNearbyEligible,
        sameDayEligible,
        shipToHomeEligible,
        bypassShowEddOnBrowseAndSearchAbTest = false
    ) => {
        const { preferredStoreName } = preferredStore || {};
        const { preferredZipCode } = user || {};
        const {
            shipToHome: { deliveryDate } = {},
            pickup: { curbsideAvailable: curbsidePickupAvailable = false, pickupStoreName = '', pickupMessage: pickupTileSubTitle = '' } = {},
            pickupNearby: { title: pickupNearbyTileTitle = '', pickupMessage: pickupNearbyTileSubTitle = '' } = {},
            sameDay: { sameDayMessage: sameDayTileSubTitle = '', sameDayShipMessage = '', sameDayTitle = '' } = {}
        } = deliveryOptions || {};
        const pickupTileEnabled = checkedRefinements.some(refinement => refinement.startsWith(PICKUP_FILTER_START));
        const pickupTileTitle = truncateText(pickupStoreName);
        let showPickupTile = !!(preferredStoreName && pickupEligible);
        let showPickupNearbyTile = !pickupEligible && pickupNearbyEligible;
        const pickupNearbyTileEnabled = pickupTileEnabled;
        let showSameDayTile = !!(deliveryOptions?.sameDay && preferredZipCode && sameDayEligible);
        const sameDayTileEnabled = checkedRefinements.some(refinement => refinement.startsWith(SAME_DAY_FILTER_START));
        const isNDDNewDeliveryNameEnabled = isNDDNewDeliveryName();
        const effectiveSameDayTitle = isNDDNewDeliveryNameEnabled ? fastDeliveryText : sameDayTitle || sameDayDeliveryText;
        const sameDayTileTitle = `${effectiveSameDayTitle}${!sameDayTileEnabled ? `: ${preferredZipCode}` : ''}`;
        let showShipToHomeTile = !!preferredZipCode && shipToHomeEligible && (showEddOnBrowseAndSearch || bypassShowEddOnBrowseAndSearchAbTest);
        const shipToHomeTileEnabled = checkedRefinements.some(refinement => refinement.startsWith(SHIP_TO_HOME_FILTER_START));
        const shipToHomeTileTitle = `${shipToHomeTitle}${!shipToHomeTileEnabled ? `: ${preferredZipCode}` : ''}`;
        const shipToHomeTileShipMessage = shipToHomeTileEnabled
            ? StringUtils.format(shipToHomeShipMessage, Date.getPromiseDate(deliveryDate, false, true), preferredZipCode)
            : '';

        // When any of the Upper Funnel Filters is checked,
        // we only display the corresponding tile in the Active state
        // if that specific filter is enabled.
        const isShopYourStoreFilterEnabled = sysUtils.shopYourStoreFilterEnabled();
        const hasActiveUpperFunnelFilter = pickupTileEnabled || sameDayTileEnabled || shipToHomeTileEnabled;

        if (isShopYourStoreFilterEnabled && hasActiveUpperFunnelFilter) {
            showPickupTile = showPickupTile && pickupTileEnabled;
            showPickupNearbyTile = showPickupNearbyTile && pickupNearbyTileEnabled;
            showSameDayTile = showSameDayTile && sameDayTileEnabled;
            showShipToHomeTile = showShipToHomeTile && shipToHomeTileEnabled;
        }

        const newProps = {
            curbsidePickupAvailable,
            curbsidePickupText,
            pickupTileEnabled,
            pickupTileSubTitle,
            pickupTileTitle,
            showPickupNearbyTile,
            pickupNearbyTileEnabled,
            pickupNearbyTileTitle,
            pickupNearbyTileSubTitle,
            sameDayTileEnabled,
            sameDayShipMessage,
            sameDayTileSubTitle,
            sameDayTileTitle,
            showPickupTile,
            showSameDayTile,
            showShipToHomeTile,
            shipToHomeTileEnabled,
            shipToHomeTileTitle,
            shipToHomeTileShipMessage
        };

        return newProps;
    }
);

const withUpperFunnelProductTilesProps = wrapHOC(connect(fields));

export {
    withUpperFunnelProductTilesProps, fields
};
